import Results from './results'
import Votes from './votes'
import toSnakeCase from '../../../util/toSnakeCase'
import GraphQLPoll from '../../graphQLPoll'
import { gql, transportEvents } from '@polleverywhere/graphql-client'
import { createAllSubscriptions } from './index.subscriptions'

const participantsDiscourseQuery = gql`
  query participantsDiscourse($participantKey: ID!) {
    participantsDiscourse(participantKey: $participantKey) {
      sharedKey
      title
      state
      titleHidden
      registeredParticipantsOnly
      anonymous
      showBranding
      showParticipants
      screenNamesEnabled
      maxVotes
      downvotingDisabled
      webEnabled
      moderationEnabled
      presenter {
        login
      }
      results {
        id
        value
        netVotes
        createdAt
        participantIdentifier
      }
      myResults {
        id
      }
      myVotes {
        resultId
        value
      }
    }
  }
`

/**
 * Update the results collection when the moderation state changes
 * Currently this just does a full refresh when enabled
 *
 * @this Discourse
 * @param {boolean} enabled New state for moderation
 */
function onModerate (enabled) {
  if (enabled) {
    this._results.set([])
  }
  this.fetch()
}

/**
 * Update the results collection when the participant identification state changes
 * There is low usage of this feature, so we just do a refresh
 *
 * @this Discourse
 * @param {boolean} enabled New state for participant identification
 */
function onIdentify (enabled) {
  if (enabled) {
    this.fetch()
  }
}

export default class Discourse extends GraphQLPoll {
  get idAttribute () {
    return 'participant_key'
  }

  type () {
    return 'discourse'
  }

  getQuery (action) {
    switch (action) {
      case 'read':
        return {
          action: 'query',
          parse: (json) => toSnakeCase(json.participantsDiscourse),
          query: participantsDiscourseQuery,
          variables: {
            participantKey: this.get('participant_key')
          }
        }
      default:
        throw new Error(`Unknown query for ${action}`)
    }
  }

  /**
   * Collection of participant submitted question results. This overrides the
   * base property to make it return a discourse results collection instead.
   * @return {Results}
   */
  get results () {
    return this._results
  }

  /**
   * Collection of results this user submitted.
   * @return {Results}
   */
  get myResults () {
    return this._myResults
  }

  /**
   * Collection of the votes this user performed on results
   * @return {Votes}
   */
  get myVotes () {
    return this._myVotes
  }

  initialize (attrs = {}) {
    super.initialize(attrs)

    if (!attrs.participant_key) {
      throw new TypeError('participant_key must be provided')
    }

    this._results = new Results(this.get('results'), { poll: this })
    this._myResults = new Results(this.get('results'), { poll: this })
    this._myVotes = new Votes(this.get('my_votes'), { poll: this })

    this.on('change:moderation_enabled', (_, value) => {
      if (Object.prototype.hasOwnProperty.call(this.previousAttributes(), 'moderation_enabled')) {
        onModerate.call(this, value)
      }
    })
    this.on('change:show_participants change:registered_participants_only', (_, value) => {
      const prev = this.previousAttributes()
      if (Object.prototype.hasOwnProperty.call(prev, 'show_participants') && Object.prototype.hasOwnProperty.call(prev, 'registered_participants_only')) {
        onIdentify.call(this, value)
      }
    })

    // We refresh all the data when the subscription transport reconnects
    // as Absinthe/Phoenix doesn't resume dead subscriptions, it creates
    // new ones if you've been fully disconnected (it can resume if briefly
    // disconnected though)
    // https://github.com/polleverywhere/conduit/issues/9
    this._gqlSubscriptionOnlineHandler = (data) => {
      if (data.reconnect) {
        this.fetch()
      }
    }
  }

  parse (data) {
    if (data.presenter && data.presenter.login) {
      // copy to legacy attribute
      data.owner_login = data.presenter.login
    }

    // All results are included in the instance API and parsed into the results
    // collection. Json key is removed so we maintain a single source of truth.
    // Use `set` to trigger events for all actions: add, update, remove.
    if (data.results) {
      this.results.set(data.results, { parse: true })
      delete data.results
    }

    if (data.my_results) {
      this.myResults.set(data.my_results, { parse: true })
      delete data.my_results
    }

    if (data.my_votes) {
      this.myVotes.set(data.my_votes, { parse: true })
      delete data.my_votes
    }

    return data
  }

  /**
   * Live update the discourse data
   * Not using instance.start as this doesn't use firehose
   */
  liveUpdate () {
    this._activeSubscriptions = createAllSubscriptions(this)
    transportEvents.on('connected', this._gqlSubscriptionOnlineHandler)
  }

  stopLiveUpdate () {
    if (this._activeSubscriptions) {
      this._activeSubscriptions.forEach(subscriptionPromise => {
        subscriptionPromise.then(s => s.unsubscribe()).catch(() => {})
      })
      this._activeSubscriptions = []
      transportEvents.off('connected', this._gqlSubscriptionOnlineHandler)
    }
  }
}
